/* eslint-disable react/jsx-no-bind */
import {
  DefaultButton,
  FontIcon,
  IButtonStyles,
  IContextualMenuItem,
  IContextualMenuProps,
  Image,
  Persona,
  PersonaSize,
  PrimaryButton,
} from 'office-ui-fabric-react';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { mobile } from '../../Styles';
import { SrOnlyText } from '../SrOnlyText/SrOnlyText';
import lilogo from '../../assets/LinkedIn-Icon.svg';
import { body1BoldFont, Body1BoldText, body2BoldFont, subhead3Font, title2Font } from '../Typography/Typography';

export enum LIConnectionStatuses {
  CONNECTED,
  UNCONNECTED,
  PENDING,
  PRE_BIND,
}

export interface IPeopleDetailHeaderProps {
  title: string;
  linkedInRank?: string;
  subtitle?: string;
  subtitleInfo?: string;
  imageUrl?: string;
  liConnectionStatus?: LIConnectionStatuses;
  onLIConnectClicked?: IContextualMenuItem['onClick'];
  onLIWrongProfile?: IContextualMenuItem['onClick'];
  onLIReportProfile?: IContextualMenuItem['onClick'];
}

const Container = styled.div`
  background-color: #283e4a;
  padding: 25px 28px 20px 28px;
  margin-bottom: 30px;
  display: grid;
  justify-items: start;
  align-items: start;
  grid-template-columns: min-content auto min-content;
  grid-template-rows: auto;
  ${mobile(`
    grid-template-columns: 1fr 1fr;
  `)}
`;

const ImageContainer = styled.div`
  position: relative;
  grid-column: 1 / 2;
  ${mobile(`
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  `)}
`;

const InfoContainer = styled.div`
  grid-column: 2 / 3;
  margin: 0px 25px;
  ${mobile(`
    grid-column: 1 / 3;
    grid-row: 2 / 3;
    margin: 20px 0 0 0;
  `)}
`;

const TitleContent = styled.h1`
  padding: 0;
  margin: 0;
  ${title2Font}
  font-weight: 200;
  line-height: 1.33;
  color: rgb(255, 255, 255);
`;

const LinkedInRankContent = styled.span`
  ${subhead3Font}
  vertical-align: middle;
`;

const SubtitleContent = styled.h2`
  ${body1BoldFont}
  color: rgb(255, 255, 255);
  margin: 6px 0 0 0;
`;

const SubtitleInfoContent = styled.h2`
  ${body1BoldFont}
  color: rgb(200, 200, 200);
  margin: 4px 0 0 0;
`;

const LIConnect = styled.div`
  align-self: center;
  justify-self: end;
  grid-column: 3 / 4;
  ${mobile(`
    grid-column: 2 / 3;
    grid-row: 1 / 2;
  `)}
`;

const StyledConnectButton = styled(PrimaryButton)`
  &:focus {
    border: 1px solid white;
  }
`;

const StyledHeadingText = styled(Body1BoldText)`
  color: white;
  display: flex;
`;

const StyledFontIcon = styled(FontIcon)`
  vertical-align: sub;
  font-size: 18px;
  padding-right: 3px;
  padding-top: 1px;
`;

const renderProfileImgSection = (props: IPeopleDetailHeaderProps) => {
  const { title, imageUrl } = props;

  return (
    <ImageContainer>
      <Persona
        text={title}
        imageUrl={imageUrl}
        size={PersonaSize.size100}
        hidePersonaDetails={true}
        styles={{ root: { height: 'auto' } }}
        imageAlt={`${title} profile`}
      />
      <Image
        src={lilogo}
        alt=""
        styles={{
          image: {
            height: 25,
            width: 25,
          },
          root: {
            position: 'absolute',
            bottom: 0,
            right: 0,
          },
        }}
      />
    </ImageContainer>
  );
};

const renderInfoSection = (props: IPeopleDetailHeaderProps) => {
  const { title, linkedInRank, subtitle, subtitleInfo } = props;
  const liRankContent = linkedInRank ? <LinkedInRankContent> • {linkedInRank}</LinkedInRankContent> : null;

  return (
    <InfoContainer>
      <TitleContent>
        <SrOnlyText>
          {title} {liRankContent} degree connection
        </SrOnlyText>
        {title}
        {liRankContent}
      </TitleContent>
      <SubtitleContent>{subtitle}</SubtitleContent>
      <SubtitleInfoContent>{subtitleInfo}</SubtitleInfoContent>
    </InfoContainer>
  );
};

const StyledMenuButton = styled(DefaultButton)`
  /* temporarily disabled until api is ready */
  display: none;
  i {
    ${body2BoldFont}
  }
`;

const renderLIConnectSection = (props: IPeopleDetailHeaderProps) => {
  const { title, onLIConnectClicked, onLIWrongProfile, onLIReportProfile, liConnectionStatus } = props;

  const activeButtonStyle = {
    backgroundColor: '#53646d',
    color: 'rgb(200, 198, 196)',
  };
  const menuButtonStyle: IButtonStyles = {
    root: {
      backgroundColor: '#283e4a',
      color: 'rgb(200, 198, 196)',
      borderWidth: 0,
      minWidth: 'auto',
      padding: 6,
      marginLeft: 6,
    },
    rootHovered: activeButtonStyle,
    rootPressed: activeButtonStyle,
    rootExpanded: activeButtonStyle,
  };

  const menuProps: IContextualMenuProps = {
    items: [
      {
        key: 'wrongProfile',
        text: 'Wrong LinkedIn profile',
        onClick: onLIWrongProfile,
      },
      {
        key: 'reportProfile',
        text: 'Report profile to LinkedIn',
        onClick: onLIReportProfile,
      },
    ],
  };

  let connectButton;
  switch (liConnectionStatus) {
    case LIConnectionStatuses.UNCONNECTED:
    case LIConnectionStatuses.PRE_BIND:
      const disabled = liConnectionStatus === LIConnectionStatuses.PRE_BIND;
      connectButton = (
        <StyledConnectButton
          text="Connect"
          disabled={disabled}
          ariaDescription={`Connect with ${title} on LinkedIn`}
          onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => onLIConnectClicked && onLIConnectClicked(e)}
        />
      );
      break;
    case LIConnectionStatuses.PENDING:
      connectButton = (
        <StyledHeadingText>
          <StyledFontIcon iconName="CheckMark" />
          <span>Pending</span>
        </StyledHeadingText>
      );
      break;
    default:
      connectButton = null;
  }

  return (
    <>
      {connectButton}
      <StyledMenuButton
        menuProps={menuProps}
        menuIconProps={{
          iconName: 'More',
        }}
        styles={menuButtonStyle}
      />
    </>
  );
};

export const PeopleDetailHeader: FunctionComponent<IPeopleDetailHeaderProps> = props => {
  return (
    <Container>
      {renderProfileImgSection(props)}
      {renderInfoSection(props)}
      <LIConnect>{renderLIConnectSection(props)}</LIConnect>
    </Container>
  );
};

export default PeopleDetailHeader;
